import { isRegionalizedSubPath } from "@lululemon/ecom-common/src/util/locale-utils";
import {
  GQL_RESPONSE_EMPTY,
  RA_CATEGORY_AND_PRODUCT_UID_EMPTY,
  RA_CATEGORY_MISMATCH,
  RA_CATEGORY_UID_EMPTY,
  RA_PID_COUNTRY_NOT_MAPPED,
  RA_PID_LOCALE_INELIGIBLE,
  RA_PID_LOCALE_REDIRECT,
  RA_PID_MISSING,
  RA_PRODUCT_AND_CATEGORY_MISMATCH,
  RA_PRODUCT_MISMATCH,
  RA_PRODUCT_UID_EMPTY,
  RA_UNIFIEDID_MISSING_CA_REDIRECT,
  RA_PID_OOS,
} from "../constants/loadDataRoutingConstants";

export const loadDataRouting = ({
  gqlProductParams,
  productDetailPageData,
  nextLocale,
  asPath,
}) => {
  const allLocalePids = productDetailPageData?.allLocalePids;
  const pid = gqlProductParams?.productId || "";
  const pidCountry = findLocaleByPid(allLocalePids, pid);
  const pdpUrl = allLocalePids?.pdpUrl;
  const redirectStatusCode = 301;
  const queryParamsExtracted = asPath.split("?")[1];
  const queryParams = queryParamsExtracted ? `?${queryParamsExtracted}` : "";
  // 1. Throw a 404 immediately if the conditions are met
  const evaluated404 =
    evaluate404EmptyResponse({
      productDetailPageData,
      pid,
    }) ||
    evaluate404CountryIneligible({
      nextLocale,
      pid,
      pidCountry,
    }) ||
    evaluate404UnifiedId({ allLocalePids, nextLocale, pid, pidCountry }) ||
    evaluate404OutOfStock({ pid, productDetailPageData });
  if (evaluated404) {
    return evaluated404;
  }

  // 2. Evaluate the subpath for the redirect
  const subpathLogic = evaluateSubpathLogic({
    nextLocale,
    pid,
    pidCountry,
  });

  // 3. Check if unified IDs in the request URL mismatches the unified IDs in the graphQL response
  const unifiedIdMismatch = getUnifiedIdMismatch({
    allLocalePids,
    gqlProductParams,
    pidCountry,
    nextLocale,
  });

  // 4. If there is a locale subpath redirect or a unified ID mismatch, redirect the guest
  if (subpathLogic.redirect || unifiedIdMismatch) {
    const { errorCode, logMessage } = consolidateErrorMessages({
      subpathLogic,
      unifiedIdMismatch,
    });

    return buildRedirectError({
      pidCountry,
      pdpUrl,
      asPath,
      queryParams,
      errorCode,
      logMessage,
      statusCode: redirectStatusCode,
      subpathLogic,
    });
  }
};

/**
 * Function to find the locale for a pid by checking if it matches any of the localePids in the provided object, or by
 * extracting it from the `siteId` property in the object.
 *
 * @param {Object} allLocalePids - An object containing the locales for a product.
 * @param {string} pid - The ID of the product.
 * @returns {?string} - The locale for the product, or null if it cannot be found.
 */
const findLocaleByPid = (allLocalePids, pid) => {
  // If allLocalePids is empty, then the productId is not recognized by ATG
  if (!allLocalePids) return null;

  // If allLocalePids is not empty and the requested productId matches a value in the object, return the key (CA or US)
  const allLocalePidCountryMatch =
    Object.keys(allLocalePids).find((key) => allLocalePids[key] === pid) ||
    null;
  if (allLocalePidCountryMatch) return allLocalePidCountryMatch;

  // If the country lookup for the PID doesn't return a match, check against the pid's siteId
  const siteIdMap = {
    na_en_us_site: "US",
    na_en_ca_site: "CA",
  };

  return siteIdMap[allLocalePids?.siteId] || null;
};

const evaluateSubpathLogic = ({ nextLocale, pid, pidCountry }) => {
  // Evaluates to 'en' or 'fr', but can scale to other languages
  const guestLanguage = nextLocale.split("-")[0];
  // Evaluates to 'US' or 'CA', but can scale to other countries
  const nextCountry = nextLocale.split("-")[1]?.toUpperCase();
  // Evaluates to the URL subpath for the locale, e.g. 'en-ca' or 'en-us' or 'fr-ca'
  const templateSubpath = `${guestLanguage}-${pidCountry.toLowerCase()}`;
  // Establish the subpath only if the templated subpath is a valid regionalized subpath
  const i18nSubPath = isRegionalizedSubPath(templateSubpath)
    ? templateSubpath
    : "";
  // Evaluates to true if the pid matches the locale
  const shouldRedirect = pidCountry !== nextCountry;

  if (shouldRedirect && i18nSubPath) {
    const loggingCode = RA_PID_LOCALE_REDIRECT;
    const loggingMessage = [
      `Request for ${pidCountry} PID ${pid} on a ${nextLocale} site will be redirected to /${i18nSubPath}/*`,
    ];

    return {
      code: loggingCode,
      i18nSubPath,
      redirect: shouldRedirect,
      logMessage: loggingMessage,
    };
  }

  // If code gets here, no locale redirect needed, but we still need to tell other redirects, like unified id mismatches,
  // which subpath they should be using in their redirects
  return {
    code: "",
    i18nSubPath,
    redirect: false,
    logMessage: [],
  };
};

const getUnifiedIdMismatch = ({
  allLocalePids = {},
  gqlProductParams,
  pidCountry,
  nextLocale,
}) => {
  const guestUrl = {
    categoryUnifiedId: gqlProductParams?.category,
    productUnifiedId: gqlProductParams?.unifiedId,
  };

  const expected = {
    categoryUnifiedId: allLocalePids?.categoryUnifiedId,
    productUnifiedId: allLocalePids?.productUnifiedId,
  };

  const caRedirect = `ATG identified the product is CA, but didn't return unifiedIds, redirecting to CA site in case data is there`;
  const categoryMismatchMessageTemplate = `Expected categoryUnifiedId ${expected.categoryUnifiedId}, but got ${guestUrl.categoryUnifiedId}`;
  const productMismatchMessageTemplate = `Expected productUnifiedId ${expected.productUnifiedId}, but got ${guestUrl.productUnifiedId}`;

  if (
    !expected.categoryUnifiedId &&
    !expected.productUnifiedId &&
    pidCountry === "CA" &&
    nextLocale === "en-us"
  ) {
    return {
      code: RA_UNIFIEDID_MISSING_CA_REDIRECT,
      logMessage: [caRedirect],
    };
  }

  if (
    guestUrl.categoryUnifiedId !== expected.categoryUnifiedId &&
    guestUrl.productUnifiedId !== expected.productUnifiedId
  ) {
    return {
      code: RA_PRODUCT_AND_CATEGORY_MISMATCH,
      logMessage: [
        categoryMismatchMessageTemplate,
        productMismatchMessageTemplate,
      ],
    };
  }

  if (guestUrl.categoryUnifiedId !== expected.categoryUnifiedId) {
    return {
      code: RA_CATEGORY_MISMATCH,
      logMessage: [categoryMismatchMessageTemplate],
    };
  }

  if (guestUrl.productUnifiedId !== expected.productUnifiedId) {
    return {
      code: RA_PRODUCT_MISMATCH,
      logMessage: [productMismatchMessageTemplate],
    };
  }
};

const consolidateErrorMessages = ({ subpathLogic, unifiedIdMismatch }) => {
  // combine the error codes
  const errorCodes = [];
  if (unifiedIdMismatch?.code) {
    errorCodes.push(unifiedIdMismatch.code);
  }
  if (subpathLogic?.code) {
    errorCodes.push(subpathLogic.code);
  }

  // combine the logging messages
  const logMessages = [];
  if (unifiedIdMismatch?.logMessage) {
    logMessages.push(...unifiedIdMismatch.logMessage);
  }
  if (subpathLogic?.logMessage) {
    logMessages.push(...subpathLogic.logMessage);
  }

  const errorCode = errorCodes.join(" | ");
  const logMessage = logMessages.join(" | ");

  return { errorCode, logMessage };
};

const evaluate404EmptyResponse = ({ productDetailPageData, pid }) => {
  if (Object.keys(productDetailPageData).length === 0) {
    const code = GQL_RESPONSE_EMPTY;
    const message = `GraphQL response was empty for PID ${pid}`;
    return build404Error(code, message);
  }
};

const evaluate404CountryIneligible = ({ nextLocale, pid, pidCountry }) => {
  if (!pid) {
    const code = RA_PID_MISSING;
    const message = `A pid was not passed into the URL, issusing a 404`;
    return build404Error(code, message);
  }

  // The productId doesn't match any of the locales
  if (!pidCountry) {
    const code = RA_PID_COUNTRY_NOT_MAPPED;
    const message = `The pid ${pid} did not match any countries, issusing a 404`;
    return build404Error(code, message);
  }

  // The productId is US and the user is in CA)
  const nextLocaleCountry = nextLocale?.split("-")[1]?.toUpperCase();
  if (pidCountry === "US" && nextLocaleCountry === "CA") {
    const code = RA_PID_LOCALE_INELIGIBLE;
    const message = `PID ${pid} belongs to ${pidCountry} and is not eligible to be shown on the ${nextLocale} site`;
    return build404Error(code, message);
  }
};

const evaluate404OutOfStock = ({ pid, productDetailPageData }) => {
  const { skus } = productDetailPageData;
  if (skus === null || skus?.length === 0) {
    const code = RA_PID_OOS;
    const message = `The PID ${pid} does not have SKUs associated with it, redirecting to an OOS 404 page`;
    return build404Error(code, message);
  }
};
const evaluate404UnifiedId = ({
  allLocalePids = {},
  nextLocale,
  pid,
  pidCountry,
}) => {
  const expected = {
    categoryUnifiedId: allLocalePids?.categoryUnifiedId,
    productUnifiedId: allLocalePids?.productUnifiedId,
    siteId: allLocalePids?.siteId,
  };

  if (
    !expected.categoryUnifiedId &&
    !expected.productUnifiedId &&
    pidCountry === "CA" &&
    nextLocale === "en-us"
  ) {
    // This is a special case where we have a PID that ATG identified as CA, but has no unifiedId.
    // ATG returns the unified IDs correctly when called with the en_CA locale.
    // Redirect is handled in the getUnifiedIdMismatch function
    return;
  }

  if (!expected.categoryUnifiedId && !expected.productUnifiedId) {
    const code = RA_CATEGORY_AND_PRODUCT_UID_EMPTY;
    const message = `PID ${pid} has no categoryUnifiedId or productUnifiedId`;
    return build404Error(code, message);
  }

  if (!expected.categoryUnifiedId) {
    const code = RA_CATEGORY_UID_EMPTY;
    const message = `PID ${pid} has no categoryUnifiedId`;
    return build404Error(code, message);
  }

  if (!expected.productUnifiedId) {
    const code = RA_PRODUCT_UID_EMPTY;
    const message = `PID ${pid} has no productUnifiedId`;
    return build404Error(code, message);
  }

  // returns undefined if no 404 is needed
};
export const build404Error = (code, message) => {
  return (
    new Error(code),
    {
      code: code,
      message: message,
      statusCode: 404,
      isRedirect: false,
      isFatal: true,
    }
  );
};

const buildRedirectError = ({
  pidCountry,
  pdpUrl,
  asPath,
  queryParams,
  errorCode,
  logMessage,
  statusCode,
  subpathLogic,
}) => {
  const { i18nSubPath } = subpathLogic;
  const noSubPathUrl = pdpUrl ? `${pdpUrl}${queryParams}` : asPath;
  const redirectUrl = i18nSubPath
    ? `/${i18nSubPath}${noSubPathUrl}`
    : noSubPathUrl;

  return (
    new Error(errorCode),
    {
      code: errorCode,
      message: logMessage,
      statusCode: statusCode,
      pidCountry,
      isRedirect: true,
      url: redirectUrl,
      isFatal: false,
    }
  );
};
